import { makeAutoObservable } from 'mobx';
import { INTERVAL } from '../../../constans/attendees-constants';
import { getActiveAttendeesCount, IGetActiveAttendeesCountPayload } from '../../../api/dao/attendee-dao';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { GetActiveAttendeesCountQuery } from '../../../graphql/API';
import { IGraphQLErrorResponse } from '../../../utils/services/error-parsing-service';
import { getPodiumInfoStore } from '../../podium-info-store/podium-info-store-instance';
import PodiumInfoStore from '../../podium-info-store/podium-info-store';
import ErrorBoundaryStore from '../../error-boundary-store/error-boundary-store';
import { getErrorBoundaryStore } from '../../error-boundary-store/error-boundary-store-instanse';
import { AttendeeStatus } from '../../../constans/attendee-enums';
import { getPermissionsStore } from '../../permissions-store/permissions-store-instance';
import PermissionsStore from '../../permissions-store/permissions-store';

interface IActivityAttendeeCountService {

  activeAttendeeNumber: number;

  setActiveAttendeesCount(count: number): void;

  setDepartureAttendeesCount(count: number): void;

  getAttendeesCountInterval(): void;

  clearAttendeesCountInterval(): void;
}

class ActivityAttendeeCountService implements IActivityAttendeeCountService {
  private numberOfActiveAttendeeWithStatusActive: number;
  private numberOfDepartureAttendee: number;
  private numberOfDisconnectedAttendee: number;
  private attendeesCountInterval: NodeJS.Timeout;

  constructor (
    private readonly errorBoundary: ErrorBoundaryStore = getErrorBoundaryStore(),
    private readonly podiumInfoStore: PodiumInfoStore = getPodiumInfoStore(),
    private readonly permissionStore: PermissionsStore = getPermissionsStore()
  ) {
    this.init();
  }

  get activeAttendeeNumber (): number {
    return this.numberOfActiveAttendeeWithStatusActive;
  }

  get departureAttendeeNumber () : number {
    return this.numberOfDepartureAttendee;
  }

  get disconnectedAttendeeNumber (): number {
    return this.numberOfDisconnectedAttendee;
  }

  private init () {
    makeAutoObservable(this);
    this.numberOfActiveAttendeeWithStatusActive = 0;
    this.numberOfDepartureAttendee = 0;
    this.numberOfDisconnectedAttendee = 0;
  }

  public setActiveAttendeesCount = (count: number): void => {
    this.numberOfActiveAttendeeWithStatusActive = count;
  };

  public setDepartureAttendeesCount = (count: number): void => {
    this.numberOfDepartureAttendee = count;
  };

  public setDisconnectedAttendeesCount = (count: number): void => {
    this.numberOfDisconnectedAttendee = count;
  };

  public getAttendeesCountInterval = (): void => {
    this.getActiveAttendeesCount();
    this.attendeesCountInterval = setInterval(() => {
      this.getActiveAttendeesCount();
    }, INTERVAL);
  };

  private generateActiveAttendeesCountPayload = (): IGetActiveAttendeesCountPayload => {
    const disconnectedFilters = this.permissionStore.isPodiumDisconnectEnabled()
      ? [
        AttendeeStatus.DISCONNECTING,
        AttendeeStatus.DISCONNECTED
      ]
      : [];
    return {
      presentationId: this.podiumInfoStore.getPresentationId()!,
      statuses: [AttendeeStatus.ACTIVE, AttendeeStatus.DEPARTED, ...disconnectedFilters]
    };
  };

  public getActiveAttendeesCount = async (): Promise<void> => {
    const presentationId: string | null = this.podiumInfoStore.getPresentationId();

    if(!presentationId) return;

    const payload: IGetActiveAttendeesCountPayload = this.generateActiveAttendeesCountPayload();

    try {
      const response: GraphQLResult<GetActiveAttendeesCountQuery> = await getActiveAttendeesCount(payload);

      if(response.data?.getActiveAttendeesCount) {
        const { data: { getActiveAttendeesCount }} = response;
        const { active, departure, disconnected } = JSON.parse(getActiveAttendeesCount);

        this.setDisconnectedAttendeesCount(disconnected);
        this.setActiveAttendeesCount(active);
        this.setDepartureAttendeesCount(departure);
      }
    } catch(error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
      this.clearAttendeesCountInterval();
    }
  };

  public clearAttendeesCountInterval = (): void => {
    clearInterval(this.attendeesCountInterval);
  };
}

export { ActivityAttendeeCountService };